import type { ApiToken, AppHealth, CreatedApiToken, CurrentUser, Ride, RidePoint, RideSummary, UploadResult } from "../types/ride";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body && !(init.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json() : undefined;

  if (!res.ok) {
    const message = (body && typeof body === "object" && "error" in body ? String(body.error) : res.statusText) || "Request failed";
    throw new ApiError(message, res.status);
  }
  return body as T;
}

export const LOGIN_URL = "/api/auth/login";

export async function getMe(): Promise<CurrentUser> {
  return request<CurrentUser>("/api/auth/me");
}

export async function getHealth(): Promise<AppHealth> {
  return request<AppHealth>("/api/health");
}

export async function logout(): Promise<{ redirectUrl: string }> {
  return request<{ redirectUrl: string }>("/api/auth/logout", { method: "POST" });
}

export async function getRides(): Promise<Ride[]> {
  return request<Ride[]>("/api/rides");
}

export async function getRide(id: string): Promise<Ride> {
  return request<Ride>(`/api/rides/${id}`);
}

export async function getRidePoints(id: string): Promise<RidePoint[]> {
  return request<RidePoint[]>(`/api/rides/${id}/points`);
}

export async function uploadRides(files: File[]): Promise<{ results: UploadResult[] }> {
  const form = new FormData();
  for (const file of files) form.append("files", file, file.name);
  return request<{ results: UploadResult[] }>("/api/rides", { method: "POST", body: form });
}

export async function renameRide(id: string, title: string): Promise<Ride> {
  return request<Ride>(`/api/rides/${id}`, { method: "PATCH", body: JSON.stringify({ title }) });
}

export async function deleteRide(id: string): Promise<void> {
  await request<void>(`/api/rides/${id}`, { method: "DELETE" });
}

export async function getSummary(): Promise<RideSummary> {
  return request<RideSummary>("/api/summary");
}

export async function getApiTokens(): Promise<ApiToken[]> {
  return request<ApiToken[]>("/api/tokens");
}

export async function createApiToken(name: string): Promise<CreatedApiToken> {
  return request<CreatedApiToken>("/api/tokens", { method: "POST", body: JSON.stringify({ name }) });
}

export async function deleteApiToken(id: string): Promise<void> {
  await request<void>(`/api/tokens/${id}`, { method: "DELETE" });
}
